"use client";

import clsx from "clsx";
import type { ReactNode } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { InfoPopover } from "../InfoPopover";

/**
 * One setting: its label, its description, its input, its default as text — and how to take the default. A setting
 * that acts at once (a bot to connect, the server's mode) has no default: its value spans both columns, or it puts a
 * control of its own (`action`) where the default stands.
 */
export interface TableRow {
  key: string;
  label: string;
  description?: string;
  input: ReactNode;
  default?: string;
  /** The value differs from the default: the default is offered to take. */
  changed?: boolean;
  /** Takes the default (absent: a value the user may not change — the default is shown only). */
  onDefault?: () => void;
  /** A setting without a default: its control in the default column (on phones after the value). */
  action?: ReactNode;
}

/**
 * A column the table leaves out on phones: its cells kept, without width and padding, their content out of the layout
 * (`PhoneHidden`). Hidden cells would not do: a column without its header cell still takes its share of the fixed
 * table, and a row without one of its cells moves the next one into its place.
 */
const NO_COLUMN_ON_PHONES = "phone:invisible phone:w-0 phone:p-0";

/** A cell's content in a column left out on phones: not laid out there (a text wrapped in no width would make the row tall). */
const PhoneHidden = ({ children }: { children: ReactNode }) => <span className="phone:hidden">{children}</span>;

/** A group of settings under one header row (without a title: no header row). */
export interface TableGroup {
  key: string;
  title?: string;
  rows: TableRow[];
}

/** A section's head: its title and (muted, wrapped to as many lines as it needs) what it is about. */
export function SectionHead({ title, description }: { title: string; description?: string }) {
  return (
    <div className="flex min-w-0 items-baseline gap-2">
      <h3 className="shrink-0 text-[12.5px] font-semibold">{title}</h3>
      {description && <p className="min-w-0 text-[11.5px] text-muted">{description}</p>}
    </div>
  );
}

/**
 * Settings in one table — setting, description, value, default — in groups under header rows, all shown, one row per
 * setting (its description whole, wrapped; a value never wraps). A setting without a default (one that acts
 * at once) has the value and default columns for its value. On phones only the essentials: the name and the value —
 * an ⓘ at the name opens the description and the default (with taking it, when the value differs).
 */
export function SettingsTable({ groups }: { groups: TableGroup[] }) {
  const { t } = useI18n();
  // the columns' widths include their padding (12 px on each side between them and to the frame): the texts keep the room they had
  return (
    <div className="mt-2 overflow-hidden rounded-lg border border-line">
      <table className="w-full table-fixed text-[12px]">
        <thead className="bg-surface-2 text-left text-[10.5px] font-semibold tracking-wide text-muted uppercase">
          <tr>
            <th className="w-[222px] truncate py-1.5 pl-3 phone:w-auto">{t("set.table.setting")}</th>
            <th className={clsx("truncate py-1.5 pl-3", NO_COLUMN_ON_PHONES)}>{t("set.table.description")}</th>
            <th className="w-[210px] truncate py-1.5 pl-3 phone:w-[40%] phone:pr-3">{t("set.table.value")}</th>
            <th className={clsx("w-[122px] truncate py-1.5 pr-3 pl-3", NO_COLUMN_ON_PHONES)}>{t("set.table.default")}</th>
          </tr>
        </thead>
        {groups.map((g) => (
          <tbody key={g.key} className="border-t border-line">
            {g.title && (
              <tr className="bg-surface-2/50">
                <td colSpan={4} className="px-3 py-1 font-semibold text-ink-2">{g.title}</td>
              </tr>
            )}
            {g.rows.map((r) => <SettingRow key={r.key} row={r} grouped={!!g.title} />)}
          </tbody>
        ))}
      </table>
    </div>
  );
}

/** One setting's line: its name (on phones an ⓘ with the description and the default), the description, the value and the default. */
function SettingRow({ row: r, grouped }: { row: TableRow; grouped: boolean }) {
  const wide = r.default === undefined && r.action === undefined;
  return (
    <tr className="border-t border-line/60 align-top hover:bg-surface-2/40">
      <td className={clsx("py-1", grouped ? "pl-6" : "pl-3")} title={r.description ? `${r.label} — ${r.description}` : r.label}>
        <span className="flex min-w-0 items-center gap-0.5">
          <span className="truncate">{r.label}</span>
          {(r.description || r.default !== undefined) && (
            <span className="shrink-0 wide:hidden">
              <InfoPopover label={r.label} compact>
                <RowInfo row={r} />
              </InfoPopover>
            </span>
          )}
        </span>
      </td>
      {/* the whole description, wrapped (the value beside it never wraps) */}
      <td className={clsx("py-1 pl-3 text-[11px] leading-snug text-muted", NO_COLUMN_ON_PHONES)}>
        <PhoneHidden>{r.description}</PhoneHidden>
      </td>
      <td className={clsx("py-1 pl-3 phone:pr-3", wide && "pr-3")} colSpan={wide ? 2 : 1}>
        <div className="flex min-w-0 items-center gap-2 whitespace-nowrap">
          {r.input}
          {r.action && <span className="shrink-0 wide:hidden">{r.action}</span>}
        </div>
      </td>
      {!wide && (
        <td className={clsx("truncate py-1 pr-3 pl-3 text-[11.5px] tabular", NO_COLUMN_ON_PHONES)}>
          <PhoneHidden>{r.action ?? <DefaultCell row={r} />}</PhoneHidden>
        </td>
      )}
    </tr>
  );
}

/** What a row's ⓘ tells on phones: its description, then its default (to take when the value differs). */
function RowInfo({ row }: { row: TableRow }) {
  const { t } = useI18n();
  return (
    <div className="space-y-2">
      {row.description && <p className="text-ink-2">{row.description}</p>}
      {row.default !== undefined && (
        <p className="flex items-center gap-1.5 text-[12px]">
          <span className="text-muted">{t("set.table.default")}:</span>
          <DefaultCell row={row} />
        </p>
      )}
    </div>
  );
}

/** A row's default: offered to take when the value differs, else shown quietly. */
function DefaultCell({ row }: { row: TableRow }) {
  const { t } = useI18n();
  if (!row.changed || !row.onDefault) return <span className="text-muted">{row.default}</span>;
  return <button type="button" onClick={row.onDefault} title={t("set.table.applyDefault")} className="cursor-pointer text-accent-ink hover:underline">{row.default}</button>;
}
